import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './Common/LoadingSpinner';

const PENDING_STATUSES = ['pending_retainer', 'pending_subscription'];

interface CustomerStatus {
  success: boolean;
  status: string | null;
  tier: string | null;
  retainerPaid: boolean;
  subscriptionStatus: string | null;
  topstepDashboardEnabled: boolean;
  topstepProvisionStatus: string | null;
  canEnterPlatform?: boolean;
}

interface CustomerGateProps {
  children: React.ReactNode;
}

function isPlatformLocked(status: CustomerStatus | null): boolean {
  if (!status?.success) return true;

  if (status.tier === 'topstep-bot') {
    return status.canEnterPlatform !== true
      || !status.retainerPaid
      || status.subscriptionStatus !== 'active'
      || status.topstepDashboardEnabled !== true
      || status.topstepProvisionStatus !== 'ready';
  }

  return PENDING_STATUSES.includes(status.status || '');
}

/**
 * Checks customer payment status for viewer-role users.
 * Blocks protected platform rendering until payment/subscription status is active.
 */
const CustomerGate: React.FC<CustomerGateProps> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<CustomerStatus | null>(null);
  const [loading, setLoading] = useState(user?.role === 'viewer');

  useEffect(() => {
    if (user?.role !== 'viewer') {
      setLoading(false);
      setStatus(null);
      return;
    }

    let active = true;
    setLoading(true);

    fetch('/api/customers/status', {
      headers: { Authorization: `Bearer ${localStorage.getItem('redex_admin_token')}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (active) setStatus(data);
      })
      .catch(() => {
        if (active) setStatus({
          success: false,
          status: null,
          tier: null,
          retainerPaid: false,
          subscriptionStatus: null,
          topstepDashboardEnabled: false,
          topstepProvisionStatus: null,
          canEnterPlatform: false,
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [user?.role, user?.email]);

  if (user?.role !== 'viewer') return <>{children}</>;
  if (loading) return <LoadingSpinner />;

  if (isPlatformLocked(status)) {
    return <Navigate to="/pending-payment" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};

export default CustomerGate;
